import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { PlayerRaidDto } from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { CombatReplay } from '@/components/CombatReplay'
import { ResourceTally } from '@/components/game/ui'
import { CombatIcon, StatIcon, ChevronRight } from '@/components/game/GameIcons'

type Perspective = 'attacker' | 'defender'

/**
 * Single PvP raid as seen by ONE participant. Reuses the M6-A5 {@link CombatReplay}
 * (the `combat` payload is the same `FleetBattleResult::toArray()` shape the NPC
 * raids serialise) and frames the outcome from the viewer's side:
 *  - attacker: "Ton raid sur X — gagné/perdu" + plundered loot.
 *  - defender: "Ton île pillée par X" / "Tu as repoussé X" + salvage on a repel.
 *
 * Loot AND salvage are always rendered as resource icons (never the words), per
 * the icons-not-words rule. `salvage` is passed in by the caller because the
 * raid DTO doesn't carry it (it lives in the notification payload); when absent
 * a repel simply shows no salvage line.
 */
export function PvpRaidReport({
  raid,
  perspective = 'attacker',
  salvage,
}: {
  raid: PlayerRaidDto
  perspective?: Perspective
  salvage?: { gold: number; wood: number }
}) {
  const [showReplay, setShowReplay] = useState(false)
  // Live ETA while the fleet is at sea — same countdown the dashboard/map use.
  const eta = useCountdown(raid.scheduledAt)

  const traveling = raid.status === 'traveling' || raid.outcome === null
  const plundered = raid.outcome === 'plundered'
  const repelled = raid.outcome === 'repelled'
  // Demi-tour sur cible devenue immunisée : résolu, mais sans bataille. Ni butin,
  // ni renflouement, ni rejeu — d'où une carte qui doit le dire explicitement,
  // sinon elle ne montre plus rien du tout.
  const immune = raid.outcome === 'immune'

  const opponent =
    perspective === 'attacker'
      ? (raid.defenderPseudo ?? raid.defenderIslandName ?? 'une île')
      : (raid.attackerPseudo ?? 'un assaillant')

  const headline = raidHeadline(perspective, raid.outcome, opponent)
  // Colour codes the raid's meaning at a glance:
  //  - white  while it's still at sea (ongoing, outcome pending — either way),
  //  - teal   for a raid that never fought (stood down: neither win nor defeat),
  //  - green  for a win from the viewer's side (my raid plundered / I repelled),
  //  - rust   for the rest (my island pillaged, my raid repelled, season cut short).
  const tone = traveling
    ? 'text-sand-100'
    : immune
      ? 'text-ocean-200'
      : (perspective === 'attacker' && plundered) || (perspective === 'defender' && repelled)
        ? 'text-green-300'
        : 'text-rum-400'

  // An incoming raid still at sea: the precise ETA + attacker composition are
  // Watchtower-gated, so the map raid-log NEVER shows the live timer here — it's a
  // teaser linking to the gated defence block on Mon île (where the Tour de guet
  // reveals what it can). Defence in depth: even if a scheduledAt leaks through,
  // this branch never renders it.
  if (perspective === 'defender' && traveling) {
    return (
      <Link
        to="/island#incoming-pvp"
        className="block rounded-lg border border-sand-500/15 bg-ocean-900/50 px-3 py-2.5 space-y-2 text-left transition-[filter] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rum-500/70"
      >
        <p className={`text-sm font-semibold flex items-center gap-1.5 ${tone}`}>
          <CombatIcon className="w-4 h-4" />
          {headline}
        </p>
        <p className="flex items-center justify-between gap-1.5 text-xs text-ocean-300">
          <span className="italic">Flotte en route…</span>
          <span className="inline-flex items-center gap-1 not-italic">
            Détail sur Mon île <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </p>
      </Link>
    )
  }

  return (
    <div className="rounded-lg border border-sand-500/15 bg-ocean-900/50 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className={`text-sm font-semibold flex items-center gap-1.5 ${tone}`}>
          <CombatIcon className="w-4 h-4" />
          {headline}
        </p>
        {raid.resolvedAt && (
          <span className="text-[10px] text-ocean-300 shrink-0">
            {new Date(raid.resolvedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        )}
      </div>

      {traveling ? (
        <p className="text-xs text-ocean-300 flex items-center gap-1.5">
          <span className="italic">Flotte en route…</span>
          {raid.scheduledAt && (
            <span className="text-ocean-200 tabular-nums not-italic">arrive dans {eta}</span>
          )}
        </p>
      ) : (
        <>
          {immune && (
            <p className="text-xs text-ocean-200 italic">
              Aucun combat. L'île venait d'être pillée, la flotte est rentrée au port.
            </p>
          )}

          {plundered && (raid.loot.gold > 0 || raid.loot.wood > 0 || raid.loot.rhum > 0) && (
            <p className="text-xs text-ocean-200 flex items-center gap-1.5">
              <span className="text-ocean-300">{perspective === 'attacker' ? 'Butin' : 'Pillé'}</span>
              <ResourceTally gold={raid.loot.gold} wood={raid.loot.wood} rhum={raid.loot.rhum} />
            </p>
          )}

          {repelled && perspective === 'defender' && salvage && (
            <p className="text-xs text-ocean-200 flex items-center gap-1.5">
              <span className="text-ocean-300">Récupéré</span>
              <ResourceTally gold={salvage.gold} wood={salvage.wood} />
            </p>
          )}

          {raid.combat && raid.combat.events.length > 0 && (
            <button
              onClick={() => setShowReplay(true)}
              className="text-xs px-3 py-1.5 rounded-md bg-rum-500/80 text-ocean-50 font-semibold hover:bg-rum-500 transition-colors inline-flex items-center gap-1.5"
            >
              <StatIcon name="abordage" className="w-3.5 h-3.5" />
              Revivre le combat
            </button>
          )}
        </>
      )}

      {showReplay && raid.combat && (
        <CombatReplay combat={raid.combat} perspective={perspective} onClose={() => setShowReplay(false)} />
      )}
    </div>
  )
}

function raidHeadline(
  perspective: Perspective,
  outcome: PlayerRaidDto['outcome'],
  opponent: string,
): string {
  if (perspective === 'attacker') {
    if (outcome === 'plundered') return `Ton raid sur ${opponent} (pillé, gagné)`
    if (outcome === 'repelled') return `Ton raid sur ${opponent} (perdu)`
    if (outcome === 'immune') return `Ton raid sur ${opponent} (demi-tour, cible immunisée)`
    if (outcome === 'season_over') return `Raid sur ${opponent} (saison terminée)`
    return `Raid sur ${opponent} en cours`
  }
  if (outcome === 'plundered') return `Ton île a été pillée par ${opponent}`
  if (outcome === 'repelled') return `Tu as repoussé ${opponent}`
  if (outcome === 'immune') return `${opponent} a fait demi-tour devant ton immunité`
  if (outcome === 'season_over') return `Attaque de ${opponent} (saison terminée)`
  return `${opponent} fait route vers ton île`
}
